'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { toast } from 'react-toastify'
import {
    MdSecurity,
    MdAdd,
    MdDelete,
    MdCheckCircle,
    MdCancel,
    MdWarning,
    MdArrowBack,
} from 'react-icons/md'
import {
    DigitalCertificate,
    CertificateStatus,
    getCertificatesByProfessional,
    revokeCertificate,
} from '@/lib/api/medical/signature-api'
import { CertificateUpload } from '@/components/signature/CertificateUpload'
import ConfirmModal from '@/components/confirm-modal'
import { useNavigate } from 'react-router-dom'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'

const STATUS_LABELS: Record<CertificateStatus, string> = {
    [CertificateStatus.CERTIFICATE_ACTIVE]: 'Ativo',
    [CertificateStatus.CERTIFICATE_EXPIRED]: 'Expirado',
    [CertificateStatus.CERTIFICATE_REVOKED]: 'Revogado',
    [CertificateStatus.CERTIFICATE_SUSPENDED]: 'Suspenso',
}

const STATUS_COLORS: Record<CertificateStatus, string> = {
    [CertificateStatus.CERTIFICATE_ACTIVE]: 'badge-success',
    [CertificateStatus.CERTIFICATE_EXPIRED]: 'badge-warning',
    [CertificateStatus.CERTIFICATE_REVOKED]: 'badge-error',
    [CertificateStatus.CERTIFICATE_SUSPENDED]: 'badge-ghost',
}

const STATUS_ICONS: Record<CertificateStatus, typeof MdCheckCircle> = {
    [CertificateStatus.CERTIFICATE_ACTIVE]: MdCheckCircle,
    [CertificateStatus.CERTIFICATE_EXPIRED]: MdWarning,
    [CertificateStatus.CERTIFICATE_REVOKED]: MdCancel,
    [CertificateStatus.CERTIFICATE_SUSPENDED]: MdWarning,
}

export default function CertificatesPage() {
    const { user, isProfessional, isCompany } = useAuth()
    const navigate = useNavigate()
    const [certificates, setCertificates] = useState<DigitalCertificate[]>([])
    const [loading, setLoading] = useState(false)
    const [showUpload, setShowUpload] = useState(false)
    const [showRevokeModal, setShowRevokeModal] = useState(false)
    const [revokeTarget, setRevokeTarget] = useState<number | null>(null)
    const [revoking, setRevoking] = useState(false)
    const [professionalId, setProfessionalId] = useState<string>('')

    // O backend já barra pelo mesmo recurso (`SignatureController`); esconder os
    // botões evita oferecer uma ação que voltaria 403.
    const { canCreate, canDelete } = useResourcePermissions(
        'registrations',
        'digital_certificates'
    )

    useEffect(() => {
        // Se é profissional, auto-selecionar pelo professionalId do usuário
        if (isProfessional && user?.professionalId) {
            setProfessionalId(user.professionalId.toString())
        }
    }, [user?.companyId, isProfessional, user?.professionalId])

    useEffect(() => {
        if (professionalId) {
            loadCertificates()
        } else {
            setCertificates([])
        }
    }, [professionalId])

    const loadCertificates = async () => {
        if (!professionalId) return

        setLoading(true)
        try {
            const response = await getCertificatesByProfessional(
                parseInt(professionalId)
            )
            if (response.success) {
                setCertificates(response.result || [])
            } else {
                toast.error(
                    response.error || 'Erro ao carregar certificados'
                )
            }
        } catch (error) {
            toast.error('Erro ao carregar certificados')
        } finally {
            setLoading(false)
        }
    }

    const handleRevoke = (id: number) => {
        setRevokeTarget(id)
        setShowRevokeModal(true)
    }

    const confirmRevoke = async () => {
        if (!revokeTarget) return

        setRevoking(true)
        try {
            const response = await revokeCertificate(revokeTarget)
            if (response.success) {
                toast.success('Certificado revogado com sucesso')
                await loadCertificates()
            } else {
                toast.error(
                    response.error || 'Erro ao revogar certificado'
                )
            }
        } catch (error) {
            toast.error('Erro ao revogar certificado')
        } finally {
            setRevoking(false)
            setShowRevokeModal(false)
            setRevokeTarget(null)
        }
    }

    const handleUploaded = () => {
        setShowUpload(false)
        loadCertificates()
    }

    // Acesso restrito: apenas company e profissional
    if (!isCompany && !isProfessional) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                    <MdSecurity className="text-4xl text-base-content/30" />
                </div>
                <div className="text-center">
                    <h3 className="text-lg font-semibold text-base-content mb-1">
                        Acesso restrito
                    </h3>
                    <p className="text-sm text-base-content/60">
                        Apenas profissionais de saúde podem gerenciar
                        certificados digitais.
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 hidden md:flex rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 items-center justify-center shadow-lg">
                            <MdSecurity className="text-2xl text-white" />
                        </div>
                        <div>
                            <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                                Certificados Digitais
                            </h1>
                            <p className="text-sm text-base-content/60">
                                Gerencie certificados ICP-Brasil
                            </p>
                        </div>
                    </div>
                </div>
                {professionalId && !showUpload && canCreate && (
                    <button
                        onClick={() => setShowUpload(true)}
                        className="btn btn-secondary gap-2"
                    >
                        <MdAdd className="text-lg" />
                        <span className="hidden sm:inline">
                            Novo Certificado
                        </span>
                    </button>
                )}
            </div>

            {/* Seletor de profissional (apenas para company) */}
            {isCompany && !isProfessional && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                    <p className="text-sm text-base-content/60 mb-3">
                        Selecione o profissional para gerenciar certificados
                    </p>
                    <ProfessionalSearchSelector
                        value={professionalId ? Number(professionalId) : null}
                        onChange={(id) => {
                            setProfessionalId(id ? String(id) : '')
                            setShowUpload(false)
                        }}
                        label=""
                        placeholder="Selecione um profissional..."
                    />
                </div>
            )}

            {/* Aguardando seleção de profissional */}
            {!professionalId && (
                <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdSecurity className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Selecione um profissional
                        </h3>
                        <p className="text-sm text-base-content/60">
                            Escolha o profissional acima para visualizar e
                            gerenciar seus certificados digitais.
                        </p>
                    </div>
                </div>
            )}

            {/* Loading certificados */}
            {loading && (
                <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                    <div className="loading loading-spinner loading-lg text-secondary"></div>
                    <p className="text-base-content/60 text-sm">
                        Carregando certificados...
                    </p>
                </div>
            )}

            {/* Conteúdo dos certificados */}
            {professionalId && !loading && (
                <>
                    {/* Upload Form */}
                    {showUpload && (
                        <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                            <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                                <h3 className="text-lg font-bold text-base-content">
                                    Registrar Certificado A1
                                </h3>
                                <p className="text-sm text-base-content/60 mt-1">
                                    Faça upload do certificado digital (.pfx ou
                                    .p12) para assinar documentos.
                                </p>
                            </div>
                            <div className="p-6">
                                <CertificateUpload
                                    professionalId={parseInt(professionalId)}
                                    onUploaded={handleUploaded}
                                    onCancel={() => setShowUpload(false)}
                                />
                            </div>
                        </div>
                    )}

                    {/* Certificates List */}
                    {certificates.length === 0 && !showUpload ? (
                        <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-8 text-center">
                            <MdSecurity className="text-4xl mx-auto mb-3 text-base-content/20" />
                            <p className="text-base-content/60 mb-4">
                                Nenhum certificado digital registrado
                            </p>
                            <button
                                onClick={() => setShowUpload(true)}
                                className="btn btn-secondary btn-sm gap-2"
                            >
                                <MdAdd className="text-lg" />
                                Registrar Certificado
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {certificates.map((cert) => {
                                const StatusIcon = STATUS_ICONS[cert.status]
                                const isActive =
                                    cert.status ===
                                    CertificateStatus.CERTIFICATE_ACTIVE
                                const isExpired =
                                    new Date(cert.validUntil) < new Date()

                                return (
                                    <div
                                        key={cert.id}
                                        className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm overflow-hidden"
                                    >
                                        <div className="p-5">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-3">
                                                        <div
                                                            className={`p-2 rounded-lg ${isActive ? 'bg-success/10' : 'bg-base-200'}`}
                                                        >
                                                            <MdSecurity
                                                                className={`text-xl ${isActive ? 'text-success' : 'text-base-content/50'}`}
                                                            />
                                                        </div>
                                                        <div>
                                                            <h3 className="font-bold text-base-content">
                                                                {cert.subject}
                                                            </h3>
                                                            <p className="text-sm text-base-content/60">
                                                                S/N:{' '}
                                                                {cert.serialNumber}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-wrap gap-2 mb-3">
                                                        <span
                                                            className={`badge ${STATUS_COLORS[cert.status]} gap-1`}
                                                        >
                                                            <StatusIcon className="text-sm" />
                                                            {
                                                                STATUS_LABELS[
                                                                cert.status
                                                                ]
                                                            }
                                                        </span>
                                                        <span className="badge badge-ghost">
                                                            {cert.type === 'ICP_BRASIL'
                                                                ? 'ICP-Brasil'
                                                                : cert.type}
                                                        </span>
                                                        {isExpired &&
                                                            isActive && (
                                                                <span className="badge badge-warning gap-1">
                                                                    <MdWarning className="text-sm" />
                                                                    Expirado
                                                                </span>
                                                            )}
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                                        <div>
                                                            <p className="text-base-content/50">
                                                                Emissor
                                                            </p>
                                                            <p className="font-medium">
                                                                {cert.issuer}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <p className="text-base-content/50">
                                                                Validade
                                                            </p>
                                                            <p className="font-medium">
                                                                {new Date(
                                                                    cert.validFrom
                                                                ).toLocaleDateString(
                                                                    'pt-BR'
                                                                )}{' '}
                                                                a{' '}
                                                                {new Date(
                                                                    cert.validUntil
                                                                ).toLocaleDateString(
                                                                    'pt-BR'
                                                                )}
                                                            </p>
                                                        </div>
                                                        {cert.crm && (
                                                            <div>
                                                                <p className="text-base-content/50">
                                                                    CRM
                                                                </p>
                                                                <p className="font-medium">
                                                                    CRM/
                                                                    {cert.crmState}{' '}
                                                                    {cert.crm}
                                                                </p>
                                                            </div>
                                                        )}
                                                        {cert.cpf && (
                                                            <div>
                                                                <p className="text-base-content/50">
                                                                    CPF
                                                                </p>
                                                                <p className="font-medium">
                                                                    {cert.cpf.replace(
                                                                        /(\d{3})(\d{3})(\d{3})(\d{2})/,
                                                                        '***.$2.***-**'
                                                                    )}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {cert.fingerprintSha256 && (
                                                        <div className="mt-3 text-xs text-base-content/40 break-all">
                                                            SHA-256:{' '}
                                                            {cert.fingerprintSha256}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="flex items-center gap-1">
                                                    {isActive && canDelete && (
                                                        <button
                                                            onClick={() =>
                                                                handleRevoke(
                                                                    cert.id
                                                                )
                                                            }
                                                            className="btn btn-ghost btn-sm btn-square text-error"
                                                            title="Revogar certificado"
                                                        >
                                                            <MdDelete className="text-lg" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </>
            )}

            <ConfirmModal
                isOpen={showRevokeModal}
                onClose={() => {
                    setShowRevokeModal(false)
                    setRevokeTarget(null)
                }}
                onConfirm={confirmRevoke}
                title="Revogar certificado"
                message="Tem certeza que deseja revogar este certificado? Esta ação é irreversível. Documentos já assinados permanecerão válidos."
                confirmButtonText="Revogar"
                cancelButtonText="Cancelar"
                loading={revoking}
            />
        </div>
    )
}
